"use client";

import { cdnCommonPath } from "@/constants/cdnUrls";
import useExternalSDK from "@/hooks/useExternalSDK";
import Image from "next/image";
import { useEffect, useRef } from "react";
import Spinner from "./ui/Spinner";

export default function GoogleSignInAndUp({
  buttonText,
  callback,
  loading = false,
}) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const sdkReady = useExternalSDK(
    () => !!window.google?.accounts?.id,
    () => {
      window.google.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
        callback: (response) => handleCredentialResponse(response),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    },
  );

  const handleCredentialResponse = async (response) => {
    try {
      const credential = response.credential;
      const base64Url = credential.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      const payload = JSON.parse(jsonPayload);

      callbackRef.current({
        email: payload.email,
        token: credential,
      });
    } catch {
      /* credential parse failed */
    }
  };

  const handleGoogleSignIn = async () => {
    if (!window.google?.accounts?.id) return;

    try {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          initiateOAuth2Flow();
        }
      });
    } catch {
      /* Google sign in failed */
    }
  };

  const initiateOAuth2Flow = () => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
      scope: "openid email profile",
      ux_mode: "popup",
      callback: async (codeResponse) => {
        if (!codeResponse.code) return;
        try {
          const res = await fetch("/api/auth/google", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: codeResponse.code }),
          });
          const data = await res.json();
          if (data.id_token) {
            // JWT parse ederek email çek
            const base64Url = data.id_token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const payload = JSON.parse(
              decodeURIComponent(
                atob(base64)
                  .split("")
                  .map(
                    (c) =>
                      "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                  )
                  .join(""),
              ),
            );
            callbackRef.current({
              email: payload.email,
              token: data.id_token, // ← artık gerçek JWT
            });
          }
        } catch {
          /* exchange failed */
        }
      },
    });

    client.requestCode();
  };

  return (
    <button
      onClick={handleGoogleSignIn}
      type="button"
      disabled={loading || !sdkReady}
      className="text-foreground border-primary-weak flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border bg-white p-3 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Image
        src={cdnCommonPath + `google-g-logo.svg`}
        alt="Google"
        width={24}
        height={24}
      />
      <span className="text-foreground font-medium">{buttonText}</span>
      {(loading || !sdkReady) && (
        <Spinner className="text-primary h-4 w-4 animate-spin" />
      )}
    </button>
  );
}
